import React, { forwardRef } from 'react';
import {
  PALETTE, FONT_SERIF, FONT_SANS, FONT_MONO,
  HaleMark, ToxIntelMark, Backplate, Card, Eyebrow, Headline, Body, Pill, FlowArrowV, Rule,
} from './primitives';

/* Asset 2 — Hale Brand Journey
 *
 * Vertical stacked workflow showing the brand's journey from application
 * to badge issuance and ongoing dashboard monitoring. Two terminal
 * outcomes (Reviewed™ / Verified™) branch from the same review pipeline. */

const VB_W = 1200;
const VB_H = 1400;

const STEPS = [
  { code: '01', label: 'Brand Application',     sub: 'Eligibility intake, contact, portfolio overview' },
  { code: '02', label: 'Document Collection',   sub: 'INCI lists, supplier docs, packaging records, claims' },
  { code: '03', label: 'Scientific Review',     sub: 'Independent toxicologist assessment via ToxIntel' },
  { code: '04', label: 'Reviewer Audit',        sub: 'QA sign-off, escalation routing, audit trail' },
];

const BadgeCard = ({ x, y, kind, color }) => (
  <g transform={`translate(${x},${y})`}>
    <Card x={0} y={0} w={420} h={150} fill="rgba(255,255,255,0.7)" stroke={color}>
      <g transform="translate(28,28)">
        <Pill x={0} y={0} label={`${kind}™`} fill={color} text={PALETTE.SLATE} />
        <text x={0} y={62} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={26}>
          Hale {kind}™ Badge
        </text>
        <text x={0} y={92} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={13}>
          Portable across retailers · embeddable on PDPs ·
        </text>
        <text x={0} y={110} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={13}>
          backed by signed memo &amp; audit trail.
        </text>
      </g>
    </Card>
  </g>
);

const BrandJourneyAsset = forwardRef(function BrandJourneyAsset(_props, ref) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hale brand journey asset"
    >
      <Backplate w={VB_W} h={VB_H} />

      {/* Header */}
      <ToxIntelMark x={64} y={70} fontSize={26} />
      <HaleMark x={200} y={42} w={84} h={36} fontSize={24} />
      <text x={VB_W - 64} y={70} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        HALE BRAND JOURNEY · PILOT LAUNCH 2026
      </text>
      <Rule x1={0} x2={VB_W} y={108} />

      <Eyebrow x={64} y={160} text="The brand journey · application to badge" />
      <Headline x={64} y={216} text="From application to portable" size={48} />
      <Headline x={64} y={266} text="retailer-ready documentation." size={48} italic color={PALETTE.CORAL} />

      {/* Vertical stack of process steps */}
      {STEPS.map((s, i) => {
        const y = 340 + i * 180;
        return (
          <g key={s.code}>
            <Card x={300} y={y} w={600} h={120}>
              <g transform="translate(28,28)">
                <Eyebrow x={0} y={0} text={s.code} />
                <text x={0} y={36} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={26}>
                  {s.label}
                </text>
                <text x={0} y={72} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={14}>
                  {s.sub}
                </text>
              </g>
            </Card>
            {i < STEPS.length - 1 && <FlowArrowV x={600} y={y + 124} h={50} />}
          </g>
        );
      })}

      {/* Branching outcome — Reviewed / Verified */}
      <FlowArrowV x={600} y={STEPS.length * 180 + 340 - 56} h={50} />
      <text
        x={600}
        y={STEPS.length * 180 + 350}
        textAnchor="middle"
        fill={PALETTE.CORAL}
        fontFamily={FONT_MONO}
        fontSize={11}
        letterSpacing={2}
      >
        BADGE ISSUANCE
      </text>

      <BadgeCard x={80}  y={STEPS.length * 180 + 380} kind="Reviewed" color={PALETTE.CORAL} />
      <BadgeCard x={700} y={STEPS.length * 180 + 380} kind="Verified" color={PALETTE.SLATE} />

      {/* Dashboard monitoring footer */}
      <Card x={80} y={STEPS.length * 180 + 560} w={1040} h={110} fill="rgba(11,20,38,0.04)">
        <g transform="translate(28,28)">
          <Eyebrow x={0} y={0} text="Continuous · post-issuance" />
          <text x={0} y={32} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={24}>
            Dashboard Monitoring
          </text>
          <text x={0} y={64} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={14}>
            Live status · reviewer assignments · outstanding items · badge progress · next action.
          </text>
        </g>
      </Card>

      <Rule x1={64} x2={VB_W - 64} y={VB_H - 60} />
      <text x={64} y={VB_H - 28} fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        HALE × TOXINTEL · INDEPENDENT SCIENTIFIC REVIEW
      </text>
      <text x={VB_W - 64} y={VB_H - 28} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        PORTABLE · DEFENSIBLE · REUSABLE
      </text>
    </svg>
  );
});

export default BrandJourneyAsset;
